import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane } from 'react-icons/fa';
import { submitContactInquiry } from '../api/contact';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  position: relative;
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/img/chicken1.jpg') center/cover no-repeat;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(107, 142, 35, 0.5) 0%, rgba(244, 164, 96, 0.5) 100%);
    z-index: 1;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ContentSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const InfoSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const InfoCard = styled.div`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
`;

const InfoTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 3px solid ${props => props.theme.colors.primary};
  padding-bottom: ${props => props.theme.spacing.md};
`;

const InfoDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.8;
  margin-bottom: ${props => props.theme.spacing.xl};
  font-size: 1.05rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.primaryLight};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: 0.95rem;
`;

const ContactValue = styled.span`
  color: ${props => props.theme.colors.textLight};
  font-size: 1rem;
  line-height: 1.6;
`;

const FormSection = styled(motion.div)`
  background: white;
  padding: ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const FormSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.xl};
  font-size: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: 0.95rem;
`;

const Required = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: ${props => props.theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: #ccc;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  min-height: 200px;
  resize: vertical;
  transition: border-color ${props => props.theme.transitions.fast};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: #ccc;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: all ${props => props.theme.transitions.fast};
  width: 100%;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: 500;
`;

const ErrorMessage = styled(motion.div)`
  background: #e74c3c;
  color: white;
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: 500;
`;

const PrivacyContent = styled.div`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.9rem;
  line-height: 1.8;

  h4 {
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-top: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.sm};
    font-size: 1rem;

    &:first-child {
      margin-top: 0;
    }
  }

  ul {
    margin: ${props => props.theme.spacing.xs} 0;
    padding-left: ${props => props.theme.spacing.xl};
  }

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  user-select: none;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const ViewDetailsButton = styled.button`
  color: ${props => props.theme.colors.primary};
  text-decoration: underline;
  font-size: 0.95rem;
  font-weight: 500;
  margin-left: ${props => props.theme.spacing.xs};
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const ModalOverlay = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.textLight};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-top: 2px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: flex-end;
`;

const ConfirmButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const ProductInquiryPage: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    content: ''
  });

  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!privacyAgreed) {
      alert(t('inquiry.privacy.alert'));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // API 호출 - content를 message로 매핑
      await submitContactInquiry({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.content
      });

      // 성공 메시지 표시
      setShowSuccess(true);

      // 폼 초기화
      setFormData({ name: '', email: '', phone: '', subject: '', content: '' });
      setPrivacyAgreed(false);

      // 3초 후 성공 메시지 숨김
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      setErrorMessage(error.message || '문의 제출 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t('contact.title')}
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t('contact.subtitle')}
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        {/* 좌측 안내문 */}
        <InfoSection
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <InfoCard>
            <InfoTitle>{t('contact.info.title')}</InfoTitle>
            <InfoDescription>
              {t('contact.info.description')}
            </InfoDescription>

            <ContactItem>
              <ContactIconWrapper>
                <FaPhone />
              </ContactIconWrapper>
              <ContactDetails>
                <ContactLabel>{t('contact.info.phone')}</ContactLabel>
                <ContactValue>{t('about.location.headquarters.tel')}</ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIconWrapper>
                <FaEnvelope />
              </ContactIconWrapper>
              <ContactDetails>
                <ContactLabel>{t('contact.info.email')}</ContactLabel>
                <ContactValue>{t('about.company.emailValue')}</ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIconWrapper>
                <FaMapMarkerAlt />
              </ContactIconWrapper>
              <ContactDetails>
                <ContactLabel>{t('contact.info.address')}</ContactLabel>
                <ContactValue>
                  {t('about.location.headquarters.address')}<br />
                  ({t('about.location.headquarters.title')})
                </ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIconWrapper>
                <FaClock />
              </ContactIconWrapper>
              <ContactDetails>
                <ContactLabel>{t('contact.info.hours')}</ContactLabel>
                <ContactValue>
                  {t('contact.info.hoursWeekday')}: 09:00 - 18:00<br />
                  {t('contact.info.hoursWeekend')}: {t('contact.info.closed')}
                </ContactValue>
              </ContactDetails>
            </ContactItem>
          </InfoCard>
        </InfoSection>

        {/* 우측 폼 */}
        <FormSection
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <FormTitle>{t('contact.form.title')}</FormTitle>
          <FormSubtitle>{t('contact.form.subtitle')}</FormSubtitle>

          {showSuccess && (
            <SuccessMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {t('contact.form.successMessage')}
            </SuccessMessage>
          )}

          {errorMessage && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {errorMessage}
            </ErrorMessage>
          )}

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                {t('contact.form.name')}
                <Required>*</Required>
              </Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('contact.form.namePlaceholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                {t('contact.form.email')}
                <Required>*</Required>
              </Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('contact.form.emailPlaceholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                {t('contact.form.phone')}
                <Required>*</Required>
              </Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t('contact.form.phonePlaceholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                {t('contact.form.subject')}
                <Required>*</Required>
              </Label>
              <Input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder={t('contact.form.subjectPlaceholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                {t('contact.form.content')}
                <Required>*</Required>
              </Label>
              <TextArea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder={t('contact.form.contentPlaceholder')}
                required
              />
            </FormGroup>

            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
              />
              <CheckboxLabel>
                {t('inquiry.privacy.agree')} <Required>*</Required>
                <ViewDetailsButton type="button" onClick={() => setShowPrivacyModal(true)}>
                  [{t('inquiry.privacy.viewDetails')}]
                </ViewDetailsButton>
              </CheckboxLabel>
            </CheckboxWrapper>

            <SubmitButton type="submit" disabled={isSubmitting}>
              <FaPaperPlane />
              {isSubmitting ? '전송 중...' : t('contact.form.submit')}
            </SubmitButton>
          </form>
        </FormSection>
      </ContentSection>

      {/* 개인정보 동의 모달 */}
      <ModalOverlay show={showPrivacyModal} onClick={() => setShowPrivacyModal(false)}>
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ModalHeader>
            <ModalTitle>{t('inquiry.privacy.modalTitle')}</ModalTitle>
            <CloseButton onClick={() => setShowPrivacyModal(false)}>×</CloseButton>
          </ModalHeader>

          <ModalBody>
            <PrivacyContent>
              <h4>{t('inquiry.privacy.section1Title')}</h4>
              <ul>
                {(t('inquiry.privacy.section1Items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h4>{t('inquiry.privacy.section2Title')}</h4>
              <ul>
                {(t('inquiry.privacy.section2Items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h4>{t('inquiry.privacy.section3Title')}</h4>
              <ul>
                {(t('inquiry.privacy.section3Items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h4>{t('inquiry.privacy.section4Title')}</h4>
              <ul>
                {(t('inquiry.privacy.section4Items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </PrivacyContent>
          </ModalBody>

          <ModalFooter>
            <ConfirmButton onClick={() => setShowPrivacyModal(false)}>
              {t('inquiry.privacy.confirm')}
            </ConfirmButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
};

export default ProductInquiryPage;
